'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Application error:', error);
    Sentry.captureException(error, {
      tags: {
        boundary: 'route-error',
      },
      extra: {
        digest: error.digest,
      },
    });
  }, [error]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    }}>
      <div style={{
        maxWidth: '600px',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '3rem',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 700,
          marginBottom: '1rem',
          color: '#fff',
        }}>
          Something went wrong
        </h1>

        <p style={{
          fontSize: '1.1rem',
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: '2rem',
          lineHeight: 1.6,
        }}>
          We encountered an unexpected error. Don&apos;t worry - your progress is saved and you can try again.
        </p>

        {process.env.NODE_ENV === 'development' && (
          <details style={{
            marginBottom: '2rem',
            textAlign: 'left',
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '1rem',
            borderRadius: '8px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
          }}>
            <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
              Error Details (Development Only)
            </summary>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {error.message}
              {error.digest && `\nError ID: ${error.digest}`}
            </pre>
          </details>
        )}

        <button
          onClick={reset}
          style={{
            background: 'linear-gradient(135deg, #d96704, #f77f00)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '1rem 2.5rem',
            fontSize: '1.05rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(217, 103, 4, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Try Again
        </button>

        <p style={{
          marginTop: '1.5rem',
          fontSize: '0.875rem',
          color: 'rgba(255, 255, 255, 0.5)',
        }}>
          If this problem persists, please refresh the page or{' '}
          <span
            onClick={() => { window.location.href = '/'; }}
            style={{
              color: '#d96704',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            return to the home page
          </span>
          .
        </p>
      </div>
    </div>
  );
}
